import { useState } from 'react';
import type { PermisoInfo } from '@copathy/shared';
import { Check, Chevrones } from './iconos';

/**
 * Selector de permisos agrupado y **plegable**. Portado de Polaris.
 *
 * ── Por qué plegado, si antes estaba todo abierto ──
 * Con 20 permisos en 13 grupos, la lista abierta es una pared de metro y medio
 * donde hay que leerlo todo para saber qué tiene marcado un rol. Plegada, con el
 * contador **«3 / 5»** en la cabecera, se sabe **dónde hay algo marcado sin abrir
 * nada** — que es exactamente lo que hace que plegar no esconda información.
 *
 * ── Y por qué la ayuda va detrás de un «?» ──
 * Antes cada permiso ocupaba dos renglones: la explicación y la clave técnica.
 * Eso triplica el alto de la lista para un texto que se lee **una vez**, cuando
 * uno duda. Detrás del `?` está a un clic, y la explicación se despliega **bajo la
 * fila** y no en un globo al pasar el ratón: en un teléfono no hay hover.
 */

/** Qué hace cada permiso, en el idioma de Copathy. */
const AYUDA: Record<string, { que: string; ojo?: string }> = {
  'empresas.ver': { que: 'Ver el listado y la ficha de los clientes.' },
  'empresas.crear': { que: 'Registrar clientes nuevos.' },
  'empresas.editar': {
    que: 'Cambiar los datos de un cliente, y los de Copathy.',
    ojo: 'Incluye activar y desactivar un cliente: al desactivarlo, su gente deja de entrar.',
  },
  'trabajadores.ver': { que: 'Ver la gente de un cliente, con su área y su cargo.' },
  'trabajadores.cargar': { que: 'Dar de alta, invitar y desactivar trabajadores.' },
  'catalogos.administrar': { que: 'Editar las listas: sectores, áreas, cargos, competencias.' },
  'usuarios.administrar': { que: 'Crear cuentas, cambiar roles y desactivar personas.' },
  'roles.administrar': {
    que: 'Cambiar qué puede hacer cada rol.',
    ojo: 'Quien tiene esto puede darse a sí mismo cualquier permiso.',
  },
  'resultados.ver_todas_empresas': { que: 'Ver resultados de cualquier cliente.' },
  'resultados.ver_propia_empresa': { que: 'Ver los resultados agregados de su propio cliente.' },
  'respuestas.ver_individuales': {
    que: 'Ver lo que respondió cada persona, con nombre y apellido.',
    ojo: 'Es lo contrario de lo que Copathy le promete al trabajador. Piénsalo dos veces.',
  },
  'participacion.ver': { que: 'Ver quién completó una actividad, sin el contenido.' },
  'auditoria.ver': { que: 'Consultar quién miró datos de qué cliente.' },
  'actividades.responder': { que: 'Responder los cuestionarios asignados.' },
  'historial.ver_propio': { que: 'Ver su propio historial de respuestas.' },
  'solicitudes.ver': { que: 'Ver la cola de quienes piden acceso.' },
  'solicitudes.decidir': { que: 'Aprobar o rechazar el acceso de alguien.' },
  'codigos.administrar': { que: 'Generar, rotar y cerrar el código de registro de un cliente.' },
  'cuestionarios.ver': { que: 'Ver la biblioteca de cuestionarios y sus versiones.' },
  'cuestionarios.armar': { que: 'Crear, editar y publicar cuestionarios.' },
};

/**
 * Permisos de alto impacto: los que hay que pensar antes de repartir.
 *
 * No es lo mismo que `prohibidoParaEmpresa`. Aquello lo **impide el motor**; esto
 * es una advertencia sobre permisos que sí se pueden dar y conviene mirar dos
 * veces antes de hacerlo.
 */
const SENSIBLES = [
  'roles.administrar',
  'usuarios.administrar',
  'respuestas.ver_individuales',
  'resultados.ver_todas_empresas',
  'solicitudes.decidir',
];

/** Agrupación por el prefijo de la clave. `empresas.*` van juntos. */
const GRUPOS: Record<string, string> = {
  empresas: 'Clientes',
  trabajadores: 'Trabajadores',
  usuarios: 'Usuarios',
  roles: 'Roles',
  catalogos: 'Catálogos',
  cuestionarios: 'Cuestionarios',
  resultados: 'Resultados',
  respuestas: 'Respuestas',
  participacion: 'Participación',
  auditoria: 'Auditoría',
  actividades: 'Actividades',
  historial: 'Historial',
  solicitudes: 'Solicitudes de acceso',
  codigos: 'Códigos de registro',
};

const grupoDe = (clave: string) => GRUPOS[clave.split('.')[0] ?? ''] ?? 'Otros';

export function SelectorPermisos({
  permisos,
  seleccion,
  editable,
  alCambiar,
}: {
  permisos: PermisoInfo[];
  seleccion: string[];
  editable: boolean;
  alCambiar: (claves: string[]) => void;
}) {
  const [abiertos, setAbiertos] = useState<Set<string>>(new Set());
  const [explicando, setExplicando] = useState<string | null>(null);

  const grupos = new Map<string, PermisoInfo[]>();
  for (const p of permisos) {
    const g = grupoDe(p.clave);
    grupos.set(g, [...(grupos.get(g) ?? []), p]);
  }
  const ordenados = [...grupos.entries()].sort(([a], [b]) => a.localeCompare(b, 'es'));

  const marcado = (clave: string) => seleccion.includes(clave);
  const alternar = (clave: string) =>
    alCambiar(marcado(clave) ? seleccion.filter((c) => c !== clave) : [...seleccion, clave]);

  function alternarGrupo(delGrupo: PermisoInfo[]) {
    const claves = delGrupo.map((p) => p.clave);
    const todos = claves.every(marcado);
    alCambiar(
      todos
        ? seleccion.filter((c) => !claves.includes(c))
        : [...new Set([...seleccion, ...claves])],
    );
  }

  return (
    <div className="space-y-2.5">
      {ordenados.map(([grupo, delGrupo]) => {
        const marcados = delGrupo.filter((p) => marcado(p.clave)).length;
        const abierto = abiertos.has(grupo);
        return (
          <section
            key={grupo}
            className="overflow-hidden rounded-2xl border border-marino-100 bg-white"
          >
            <div className="flex items-center gap-2 px-3 py-2.5">
              <button
                type="button"
                aria-expanded={abierto}
                onClick={() =>
                  setAbiertos((previo) => {
                    const s = new Set(previo);
                    if (s.has(grupo)) s.delete(grupo);
                    else s.add(grupo);
                    return s;
                  })
                }
                className="flex min-w-0 flex-1 items-center gap-2 rounded-xl text-left
                  focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
              >
                <Chevrones
                  size={16}
                  className={`shrink-0 text-marino-300 transition ${abierto ? 'rotate-180' : ''}`}
                />
                <span className="truncate font-semibold text-marino-800">{grupo}</span>
                {/* El contador es lo que hace que plegar no esconda información. */}
                <span
                  className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                    marcados === 0
                      ? 'bg-marino-50 text-marino-400'
                      : 'bg-naranja-100 text-marino-800'
                  }`}
                >
                  {marcados} / {delGrupo.length}
                </span>
              </button>
              {editable && (
                <button
                  type="button"
                  onClick={() => alternarGrupo(delGrupo)}
                  className="shrink-0 rounded-lg px-2 py-1 text-xs font-semibold text-marino-600
                    transition hover:text-naranja-600 hover:underline
                    focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
                >
                  {marcados === delGrupo.length ? 'Quitar todos' : 'Marcar todos'}
                </button>
              )}
            </div>

            {abierto && (
              <ul className="space-y-1 border-t border-marino-100 p-2">
                {delGrupo.map((p) => {
                  const activo = marcado(p.clave);
                  const ayuda = AYUDA[p.clave];
                  return (
                    <li key={p.clave} className={`rounded-xl ${activo ? 'bg-naranja-50/60' : ''}`}>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => editable && alternar(p.clave)}
                          aria-pressed={activo}
                          disabled={!editable}
                          className={`flex min-h-11 min-w-0 flex-1 items-center gap-2.5 rounded-xl px-2.5
                            text-left text-sm transition disabled:cursor-default
                            focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15
                            ${activo ? 'text-marino-800' : 'text-marino-500 hover:bg-marino-50'}`}
                        >
                          <span
                            className={`grid h-5 w-5 shrink-0 place-items-center rounded-md border transition
                              ${
                                activo
                                  ? 'border-naranja-500 bg-naranja-500 text-marino-900'
                                  : 'border-marino-300 bg-white'
                              }`}
                          >
                            {activo && <Check size={13} />}
                          </span>
                          <span className="min-w-0 truncate">{p.descripcion}</span>
                          {SENSIBLES.includes(p.clave) && (
                            <span
                              title="Permiso de alto impacto"
                              aria-label="Permiso de alto impacto"
                              className="shrink-0 text-amber-500"
                            >
                              ⚠
                            </span>
                          )}
                        </button>
                        {ayuda && (
                          <button
                            type="button"
                            aria-label={`Qué hace «${p.descripcion}»`}
                            aria-expanded={explicando === p.clave}
                            onClick={() =>
                              setExplicando(explicando === p.clave ? null : p.clave)
                            }
                            className={`mr-1 grid h-6 w-6 shrink-0 place-items-center rounded-full border
                              text-[11px] font-bold transition
                              focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15
                              ${
                                explicando === p.clave
                                  ? 'border-naranja-500 bg-naranja-500 text-marino-900'
                                  : 'border-marino-200 text-marino-400 hover:border-naranja-400 hover:text-naranja-600'
                              }`}
                          >
                            ?
                          </button>
                        )}
                      </div>

                      {ayuda && explicando === p.clave && (
                        <div className="mx-2.5 mb-2 rounded-xl border border-marino-100 bg-marino-50/70 px-3 py-2.5 text-xs leading-relaxed">
                          <p className="text-marino-600">{ayuda.que}</p>
                          {ayuda.ojo && <p className="mt-1.5 text-amber-700">⚠ {ayuda.ojo}</p>}
                          <p className="mt-1.5 font-mono text-[11px] text-marino-400">{p.clave}</p>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
